import { defineStore } from 'pinia';
import type { Channel } from 'pusher-js';
import Pusher from 'pusher-js';

import { stuut } from '@/api';
import { useEmailProcessingStore } from '@/store/emailProcessing';
import useProfile from '@/store/profile';
import { useStatusCounts } from '@/store/statusCounts';

interface State {
  callProcessingChannel: Channel | null;
  client: Pusher | null;
  emailProcessingChannel: Channel | null;
  orgChannel: Channel | null;
  userChannel: Channel | null;
}

const orgEvents: Record<string, (data: Record<string, string>) => void> = {
  'disputes.counts_updated': (data) => {
    useStatusCounts().onPusherEvent('disputes', data.last_created_at);
  },
  'inbox.counts_updated': (data) => {
    useStatusCounts().onPusherEvent('inbox', data.last_created_at);
  },
  'profile.updated': () => {
    const profileStore = useProfile();
    profileStore.fetchProfile(true);
  },
  'tasks.counts_updated': (data) => {
    useStatusCounts().onPusherEvent('tasks', data.last_created_at);
  },
};

export const usePusher = defineStore('pusher', {
  actions: {
    disconnect() {
      this.client?.disconnect();
      this.client = null;
      this.orgChannel = null;
      this.userChannel = null;
      this.emailProcessingChannel = null;
      this.callProcessingChannel = null;
    },
    initialize() {
      if (
        !process.env.STUUT_PUSHER_APP_KEY ||
        !process.env.STUUT_PUSHER_APP_CLUSTER ||
        !process.env.STUUT_PUSHER_APP_ENDPOINT
      ) {
        console.warn('Pusher environment variables are not set');
        return;
      }

      const profileStore = useProfile();
      const profile = profileStore.profile;

      if (!profile) {
        return;
      }

      if (stuut.authHeader === null) {
        throw new Error('No user token');
      }
      this.client = new Pusher(process.env.STUUT_PUSHER_APP_KEY, {
        channelAuthorization: {
          endpoint: process.env.STUUT_PUSHER_APP_ENDPOINT,
          headers: {
            Authorization: stuut.authHeader,
          },
          transport: 'ajax',
        },
        cluster: process.env.STUUT_PUSHER_APP_CLUSTER,
      });

      this.userChannel = this.client.subscribe(`private-${profile.id}`);
      this.orgChannel = this.client.subscribe(`private-${profile.organization.id}`);
      const emailProcessingChannelName = `private-email-processing-${profile.organization.id}`;
      this.emailProcessingChannel = this.client.subscribe(emailProcessingChannelName);

      const callProcessingChannelName = `private-call-processing-${profile.organization.id}`;
      this.callProcessingChannel = this.client.subscribe(callProcessingChannelName);

      for (const event in orgEvents) {
        this.orgChannel?.bind(event, orgEvents[event]);
      }

      useEmailProcessingStore().initialize();
    },
  },
  state: (): State => ({
    callProcessingChannel: null,
    client: null,
    orgChannel: null,
    userChannel: null,
    emailProcessingChannel: null,
  }),
});
